import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Plus, Search, DollarSign, CreditCard } from 'lucide-react';
import { toast } from 'react-hot-toast';
import api from '../services/api';

export default function Pagos() {
  const [showModal, setShowModal] = useState(false);
  const queryClient = useQueryClient();

  const { data: pagos, isLoading } = useQuery({
    queryKey: ['pagos'],
    queryFn: async () => {
      const response = await api.get('/pagos');
      return response.data;
    },
  });

  const createMutation = useMutation({
    mutationFn: (data) => api.post('/pagos', data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['pagos'] });
      queryClient.invalidateQueries({ queryKey: ['facturas'] });
      toast.success('Pago registrado exitosamente');
      setShowModal(false);
    },
    onError: () => toast.error('Error al registrar pago'),
  });

  const getMetodoIcon = (metodo) => {
    switch (metodo) {
      case 'efectivo': return '💵';
      case 'transferencia': return '🏦';
      case 'mercadopago': return '📱';
      case 'tarjeta': return '💳';
      default: return '💰';
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const formData = new FormData(e.currentTarget);
    const data = {
      facturaId: formData.get('facturaId'),
      monto: Number(formData.get('monto')),
      metodo: formData.get('metodo'),
      referencia: formData.get('referencia'),
      notas: formData.get('notas'),
    };
    createMutation.mutate(data);
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-3xl font-bold text-gray-900">Pagos</h1>
        <button
          onClick={() => setShowModal(true)}
          className="flex items-center space-x-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700"
        >
          <Plus size={20} />
          <span>Registrar Pago</span>
        </button>
      </div>

      <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
        <table className="w-full">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Factura</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Cliente</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Monto</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Metodo</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Fecha</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Referencia</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {pagos?.map((pago) => (
              <tr key={pago.id} className="hover:bg-gray-50">
                <td className="px-6 py-4 font-medium text-gray-900">{pago.factura?.numeroFactura}</td>
                <td className="px-6 py-4 text-gray-600">
                  {pago.factura?.cliente?.nombre} {pago.factura?.cliente?.apellido}
                </td>
                <td className="px-6 py-4 font-bold text-green-600">
                  ${Number(pago.monto).toLocaleString('es-AR')}
                </td>
                <td className="px-6 py-4">
                  <span className="inline-flex items-center space-x-1">
                    <span>{getMetodoIcon(pago.metodo)}</span>
                    <span className="capitalize">{pago.metodo}</span>
                  </span>
                </td>
                <td className="px-6 py-4 text-gray-600">
                  {new Date(pago.fechaPago).toLocaleDateString('es-AR')}
                </td>
                <td className="px-6 py-4 text-gray-500 text-sm">{pago.referencia || '-'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl p-8 w-full max-w-md">
            <h2 className="text-2xl font-bold text-gray-900 mb-6">Registrar Pago</h2>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">ID Factura</label>
                <input name="facturaId" required className="w-full px-3 py-2 border rounded-lg" placeholder="UUID de la factura" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Monto ($)</label>
                <input name="monto" type="number" step="0.01" required className="w-full px-3 py-2 border rounded-lg" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Metodo de Pago</label>
                <select name="metodo" required className="w-full px-3 py-2 border rounded-lg">
                  <option value="efectivo">💵 Efectivo</option>
                  <option value="transferencia">🏦 Transferencia</option>
                  <option value="mercadopago">📱 MercadoPago</option>
                  <option value="tarjeta">💳 Tarjeta</option>
                  <option value="otro">💰 Otro</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Referencia</label>
                <input name="referencia" className="w-full px-3 py-2 border rounded-lg" placeholder="N° transferencia, etc." />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Notas</label>
                <textarea name="notas" rows={2} className="w-full px-3 py-2 border rounded-lg"></textarea>
              </div>
              <div className="flex justify-end space-x-3 mt-6">
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  disabled={createMutation.isPending}
                  className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50"
                >
                  {createMutation.isPending ? 'Registrando...' : 'Registrar Pago'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
